/**
 * Phase 6i — Trunk provider presets.
 *
 * Each preset describes the SHAPE of the trunk editor for a specific
 * carrier (voip.ms, sip.us, Telnyx, ...). The engine itself remains
 * carrier-agnostic — it only reads `username`, `password`, `host`, etc.
 * The preset is purely a UX layer:
 *
 *   - Hides irrelevant fields (e.g. sip.us doesn't need sub-accounts).
 *   - Pre-fills sensible defaults (host, port, transport, register
 *     toggle) so onboarding a new trunk is one screen, not a tutorial.
 *   - Owns the rule for *building* the canonical SIP `username` from
 *     human inputs — voip.ms's `<account>_<sub>` pattern is the most
 *     interesting case; everyone else just maps the user-input to the
 *     username 1:1.
 *
 * Adding a new carrier is intentionally cheap — drop in a new entry
 * here, no backend change needed. The Trunk model already accepts
 * `provider_preset`, `provider_account`, `provider_subaccount` plus
 * the canonical fields, so any preset's `buildUsername` output flows
 * straight to the wire.
 */

export const PROVIDER_PRESETS = {
  generic: {
    id: "generic",
    label: "Custom / generic",
    summary: "Full manual control. All fields visible. Pick this if your carrier isn't listed.",
    fields: [
      "host",
      "port",
      "transport",
      "auth_mode",
      "username",
      "password",
      "from_user",
    ],
    defaults: {},
    buildUsername: ({ username }) => (username || "").trim(),
    helpers: {},
  },

  voipms: {
    id: "voipms",
    label: "VoIP.ms (sub-account)",
    summary:
      "Sub-account model. Master account + sub-account suffix combine as <account>_<sub>. Requires REGISTER in user/password mode.",
    fields: ["account", "subaccount", "password", "host"],
    defaults: {
      host: "tampa1.voip.ms",
      port: 5060,
      transport: "udp",
      auth_mode: "digest",
      register_with_carrier: true,
      register_expiry_seconds: 300,
      use_srv: false,
    },
    buildUsername: ({ provider_account, provider_subaccount }) => {
      const a = (provider_account || "").trim();
      const s = (provider_subaccount || "").trim();
      if (!a) return s;
      if (!s) return a;
      // Operators commonly paste the FULL sub-account string from
      // voip.ms (e.g. `118011_wzolt`) into the Sub-account field
      // instead of just the suffix `wzolt`. Detect that case and don't
      // double-prefix — `118011_118011_wzolt` is invalid and would 407.
      if (s === a || s.startsWith(`${a}_`)) return s;
      return `${a}_${s}`;
    },
    helpers: {
      account: "Master account number from voip.ms portal (e.g. 118011)",
      subaccount:
        "Either the suffix only (e.g. `wzolt`) or the full sub-account name (e.g. `118011_wzolt`) — both work, the engine deduplicates the prefix.",
      host: "Pick the PoP nearest your VPS — tampa1, atlanta1, dallas1, seattle1, london1, etc.",
      password: "Generated in the voip.ms sub-account settings page.",
    },
  },

  sipus: {
    id: "sipus",
    label: "SIP.us",
    summary:
      "Account ID = auth username = registered AoR. Defaults to user/password mode with REGISTER. Phase 6w made SRV mode work cleanly with SIP.us — the engine keeps the logical domain (gw.sip.us) in the R-URI/From URI to match the digest realm and resolves only at the transport layer (per RFC 3263). If you ever need to pin to a specific gateway for diagnostics, switch host to gw4.sip.us / gw5.sip.us and toggle use_srv off.",
    fields: ["account", "password", "host"],
    aliasFields: { account: "Account / Username" },
    defaults: {
      // Phase 6w — back to SRV pool now that the engine handles SRV
      // correctly (logical-domain in headers, resolved gateway only
      // at transport). Operators can still pin to gw4/gw5 manually
      // for diagnostics; SIP.us's official guide recommends the
      // specific gateway form, but Vodia + Asterisk + FreeSWITCH all
      // run cleanly on the SRV pool with the standards-compliant
      // header/transport separation.
      host: "gw.sip.us",
      port: 5060,
      transport: "udp",
      auth_mode: "digest",
      register_with_carrier: true,
      register_expiry_seconds: 300,
      use_srv: true,
    },
    buildUsername: ({ provider_account }) => (provider_account || "").trim(),
    helpers: {
      account:
        "Your sip.us account ID (provided in the sip.us welcome email — typically a 10-digit number). This is also the registered AoR — outbound INVITEs use it as the From URI user-part; the customer's caller-ID number goes in P-Asserted-Identity automatically.",
      host: "gw.sip.us is the SRV pool — engine resolves it to gw1/gw4/gw5 at the transport layer and keeps the logical domain in the R-URI/From URI (matches digest realm). To pin to a specific gateway for diagnostics, switch this to gw4.sip.us or gw5.sip.us and toggle use_srv off.",
    },
  },

  telnyx: {
    id: "telnyx",
    label: "Telnyx Elastic SIP",
    summary:
      "Connection-based. Pull the SIP credentials from your Telnyx Mission Control portal → Connections.",
    fields: ["username", "password", "host"],
    defaults: {
      host: "sip.telnyx.com",
      port: 5060,
      transport: "udp",
      auth_mode: "digest",
      register_with_carrier: true,
      register_expiry_seconds: 300,
      use_srv: false,
    },
    buildUsername: ({ username }) => (username || "").trim(),
    helpers: {
      username:
        'The "SIP Username" from your Telnyx Connection (NOT the Connection ID).',
      password: 'The "SIP Password" from your Telnyx Connection.',
      host: "sip.telnyx.com is the universal endpoint.",
    },
  },

  flowroute: {
    id: "flowroute",
    label: "Flowroute",
    summary:
      "Tech prefix + auth credentials. Register-required by default in user/password mode.",
    fields: ["username", "password", "host"],
    defaults: {
      host: "sip.flowroute.com",
      port: 5060,
      transport: "udp",
      auth_mode: "digest",
      register_with_carrier: true,
      register_expiry_seconds: 300,
      use_srv: false,
    },
    buildUsername: ({ username }) => (username || "").trim(),
    helpers: {
      username: "From your Flowroute Manage portal — Interconnection → SIP Settings.",
      host: "sip.flowroute.com is the universal endpoint.",
    },
  },

  twilio: {
    id: "twilio",
    label: "Twilio Elastic SIP",
    summary:
      "Per-trunk endpoint at <name>.pstn.twilio.com. Twilio prefers IP authentication — toggle register-with-carrier only if you've set up credential lists.",
    fields: ["username", "password", "host"],
    defaults: {
      host: "<your-trunk-name>.pstn.twilio.com",
      port: 5060,
      transport: "tls",
      auth_mode: "digest",
      register_with_carrier: false,
      use_srv: false,
    },
    buildUsername: ({ username }) => (username || "").trim(),
    helpers: {
      host: "Replace <your-trunk-name> with the Termination URI from the Twilio console.",
      username: "From a Twilio Credential List (or leave blank if using IP auth).",
    },
  },

  bandwidth: {
    id: "bandwidth",
    label: "Bandwidth.com",
    summary:
      "Wholesale carrier — almost always IP-authenticated, no REGISTER. Allowlist your VPS public IP in the Bandwidth dashboard.",
    fields: ["host", "from_user"],
    defaults: {
      host: "g.com.bandwidth.com",
      port: 5060,
      transport: "udp",
      auth_mode: "ip",
      register_with_carrier: false,
      use_srv: false,
    },
    buildUsername: () => "",
    helpers: {
      host: "Bandwidth assigns you a host like g.com.bandwidth.com or your subdomain.",
      from_user: "Your Bandwidth account/sub-account identifier — used as the From-user.",
    },
  },

  skyetel: {
    id: "skyetel",
    label: "Skyetel",
    summary:
      "IP-authenticated wholesale carrier. Add your VPS IP to your Endpoint Group on the Skyetel portal.",
    fields: ["host", "from_user"],
    defaults: {
      host: "sip.skyetel.com",
      port: 5060,
      transport: "udp",
      auth_mode: "ip",
      register_with_carrier: false,
      use_srv: true,
    },
    buildUsername: () => "",
    helpers: {
      host: "Default sip.skyetel.com — Skyetel publishes SRV so SRV mode picks the closest PoP.",
      from_user: "Optional — used as the From-user when no per-call CID is rewritten.",
    },
  },

  plivo: {
    id: "plivo",
    label: "Plivo Zentrunk",
    summary:
      "Per-trunk credentials. Pulled from your Plivo console under Voice → Zentrunk → SIP Trunks.",
    fields: ["username", "password", "host"],
    defaults: {
      host: "phone.plivo.com",
      port: 5060,
      transport: "udp",
      auth_mode: "digest",
      register_with_carrier: true,
      register_expiry_seconds: 300,
      use_srv: false,
    },
    buildUsername: ({ username }) => (username || "").trim(),
    helpers: {
      username: "Trunk-specific SIP username from Plivo console.",
      host: "phone.plivo.com is the universal endpoint.",
    },
  },

  inteliquent: {
    id: "inteliquent",
    label: "Inteliquent / Onvoy",
    summary:
      "Wholesale tier-1 carrier — strict IP authentication. No REGISTER, no digest. Allowlist your VPS IP via your Inteliquent Service Engineer.",
    fields: ["host", "from_user"],
    defaults: {
      host: "sip.inteliquent.com",
      port: 5060,
      transport: "udp",
      auth_mode: "ip",
      register_with_carrier: false,
      use_srv: false,
    },
    buildUsername: () => "",
    helpers: {
      host: "Inteliquent normally provides a session-border-controller hostname specific to your account.",
    },
  },

  vonage: {
    id: "vonage",
    label: "Vonage Business / SIP Trunking",
    summary:
      "Vonage Business SIP Trunks — per-trunk credentials, digest auth, register-required.",
    fields: ["username", "password", "host"],
    defaults: {
      host: "sbc.vonagebusiness.com",
      port: 5060,
      transport: "udp",
      auth_mode: "digest",
      register_with_carrier: true,
      register_expiry_seconds: 300,
      use_srv: false,
    },
    buildUsername: ({ username }) => (username || "").trim(),
    helpers: {
      username: "Trunk username from your Vonage Business admin portal.",
      host: "Vonage assigns a per-tenant SBC hostname; sbc.vonagebusiness.com is a common default.",
    },
  },

  anveo: {
    id: "anveo",
    label: "Anveo / 46Labs",
    summary:
      "Anveo Business SIP — username/password digest auth, register-required.",
    fields: ["username", "password", "host"],
    defaults: {
      host: "sip.anveo.com",
      port: 5060,
      transport: "udp",
      auth_mode: "digest",
      register_with_carrier: true,
      register_expiry_seconds: 300,
      use_srv: false,
    },
    buildUsername: ({ username }) => (username || "").trim(),
    helpers: {
      username: "From your Anveo Direct portal — Trunks → SIP Credentials.",
    },
  },

  signalwire: {
    id: "signalwire",
    label: "SignalWire",
    summary:
      "SignalWire SIP — per-domain credentials. Username + password from your SignalWire Space → SIP Endpoints.",
    fields: ["username", "password", "host"],
    defaults: {
      host: "sip.signalwire.com",
      port: 5060,
      transport: "udp",
      auth_mode: "digest",
      register_with_carrier: true,
      register_expiry_seconds: 300,
      use_srv: false,
    },
    buildUsername: ({ username }) => (username || "").trim(),
    helpers: {
      host: "Replace with <your-space-name>.sip.signalwire.com if you've set up a private space.",
    },
  },
};

export const PRESET_LIST = [
  PROVIDER_PRESETS.generic,
  PROVIDER_PRESETS.voipms,
  PROVIDER_PRESETS.sipus,
  PROVIDER_PRESETS.telnyx,
  PROVIDER_PRESETS.flowroute,
  PROVIDER_PRESETS.twilio,
  PROVIDER_PRESETS.bandwidth,
  PROVIDER_PRESETS.skyetel,
  PROVIDER_PRESETS.plivo,
  PROVIDER_PRESETS.inteliquent,
  PROVIDER_PRESETS.vonage,
  PROVIDER_PRESETS.anveo,
  PROVIDER_PRESETS.signalwire,
];

/**
 * Resolve the preset for a stored trunk. Falls back to "generic" for
 * legacy trunks (no preset set) so the editor still renders something
 * sensible.
 */
export function getPreset(id, customPresets = []) {
  if (!id) return PROVIDER_PRESETS.generic;
  const builtin = PROVIDER_PRESETS[id];
  if (builtin) return builtin;
  // Phase 6i (cust) — search tenant-defined custom presets.
  const c = (customPresets || []).find((p) => p.id === id);
  if (c) return materializeCustomPreset(c);
  return PROVIDER_PRESETS.generic;
}

/**
 * Build the canonical SIP username from current form state, given a
 * preset id. Always returns a string (possibly empty).
 */
export function buildUsername(presetId, form, customPresets = []) {
  const preset = getPreset(presetId, customPresets);
  try {
    return (preset.buildUsername(form) || "").trim();
  } catch {
    return (form?.username || "").trim();
  }
}

/**
 * Phase 6i (cust) — convert a server-side custom preset record into the
 * runtime shape used by the editor (with a `buildUsername` closure).
 */
export function materializeCustomPreset(server) {
  const tpl = server.username_template || "{username}";
  return {
    id: server.id,
    isCustom: true,
    label: server.name,
    summary: server.summary || "",
    fields: server.fields || [],
    aliasFields: server.aliases || {},
    defaults: server.defaults || {},
    helpers: server.helpers || {},
    buildUsername: (form) => {
      // Substitute `{key}` placeholders against the form. Unknown keys
      // resolve to empty string (matches Python's str.format with a
      // safe-dict).
      return tpl.replace(/\{(\w+)\}/g, (_, k) => {
        const v = form?.[k];
        return v == null ? "" : String(v).trim();
      });
    },
  };
}
